import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { listCreatives, updateCreative } from '@/services/creatives';
import type { Creative } from '@/types/api';

interface AssignCreativeModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaignId: number | null;
  campaignName: string;
  onAssigned?: () => void;
}

export default function AssignCreativeModal({
  isOpen,
  onClose,
  campaignId,
  campaignName,
  onAssigned,
}: AssignCreativeModalProps) {
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    listCreatives({ limit: 100 })
      .then((page) => {
        const eligible = page.items.filter((cr) => {
          if (cr.id == null || campaignId == null) return false;
          const assigned = cr.assigned_campaign_ids ?? [];
          return !assigned.includes(campaignId);
        });
        setCreatives(eligible);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load creatives'))
      .finally(() => setLoading(false));
  }, [campaignId]);

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    load();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, load, onClose]);

  async function handleAssign(creativeId: number, existingCampaignIds: number[]) {
    if (campaignId == null) return;
    setAssigningId(creativeId);
    try {
      const nextIds = existingCampaignIds.includes(campaignId)
        ? existingCampaignIds
        : [...existingCampaignIds, campaignId];
      await updateCreative(creativeId, { assigned_campaign_ids: nextIds });
      onAssigned?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to assign');
    } finally {
      setAssigningId(null);
    }
  }

  if (!isOpen) return null;

  const modalContent = (
    <div className="aspectly:fixed aspectly:inset-0 aspectly:z-[9999] aspectly:flex aspectly:items-center aspectly:justify-center aspectly:p-4">
      <div
        className="aspectly:absolute aspectly:inset-0 aspectly:bg-on-surface/30 aspectly:backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="aspectly:relative aspectly:z-10 aspectly:w-full aspectly:max-w-lg aspectly:bg-surface-container-lowest aspectly:rounded-2xl aspectly:shadow-2xl aspectly:overflow-hidden aspectly:border aspectly:border-outline-variant/10">
        <div className="aspectly:flex aspectly:items-center aspectly:justify-between aspectly:px-6 aspectly:py-5 aspectly:border-b aspectly:border-outline-variant/10">
          <div>
            <h2 className="aspectly:font-headline aspectly:font-bold aspectly:text-on-surface aspectly:text-lg">
              Assign Creative
            </h2>
            <p className="aspectly:text-sm aspectly:text-on-surface-variant aspectly:mt-0.5 aspectly:truncate">
              {campaignName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="aspectly:w-9 aspectly:h-9 aspectly:flex aspectly:items-center aspectly:justify-center aspectly:rounded-full aspectly:hover:bg-surface-container aspectly:text-on-surface-variant aspectly:hover:text-on-surface aspectly:transition-colors aspectly:cursor-pointer"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="aspectly:p-6 aspectly:space-y-3 aspectly:max-h-[60vh] aspectly:overflow-y-auto">
          {error && (
            <div className="aspectly:p-3 aspectly:rounded-lg aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="aspectly:flex aspectly:justify-center aspectly:py-10">
              <span className="material-symbols-outlined aspectly:animate-spin aspectly:text-primary aspectly:text-4xl">
                progress_activity
              </span>
            </div>
          ) : creatives.length === 0 ? (
            <p className="aspectly:text-on-surface-variant aspectly:text-sm aspectly:py-4 aspectly:text-center">
              No unassigned creatives available. Create a new creative instead.
            </p>
          ) : (
            <ul className="aspectly:space-y-1.5">
              {creatives.map((cr) => (
                <li key={cr.id}>
                  <button
                    type="button"
                    disabled={assigningId != null}
                    onClick={() => handleAssign(cr.id!, cr.assigned_campaign_ids ?? [])}
                    className="aspectly:w-full aspectly:text-left aspectly:px-4 aspectly:py-3 aspectly:rounded-xl aspectly:border aspectly:border-outline-variant/20 aspectly:text-on-surface aspectly:text-sm aspectly:font-medium aspectly:hover:bg-primary-container/30 aspectly:hover:border-primary/30 aspectly:transition-all aspectly:cursor-pointer aspectly:disabled:opacity-50 aspectly:flex aspectly:items-center aspectly:justify-between aspectly:gap-2"
                  >
                    <div className="aspectly:flex aspectly:items-center aspectly:gap-3 aspectly:min-w-0">
                      <span className="material-symbols-outlined aspectly:text-primary aspectly:text-lg aspectly:shrink-0">
                        auto_awesome
                      </span>
                      <span className="aspectly:truncate">{cr.name}</span>
                    </div>
                    {assigningId === cr.id && (
                      <span className="material-symbols-outlined aspectly:animate-spin aspectly:text-primary aspectly:text-xl aspectly:shrink-0">
                        progress_activity
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
